import { datadogLogs } from "@datadog/browser-logs";
import { type ClassValue, clsx } from "clsx";
import { isEqual } from "date-fns";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function throwIfNull<T>(
  value: T | null | undefined,
  errorMessage?: string,
) {
  if (value === null || value === undefined) {
    throw new Error(errorMessage);
  }
  return value;
}

export function splitArray<T>(array: T[], filterFn: (item: T) => boolean) {
  return array.reduce(
    (accum, item) =>
      filterFn(item)
        ? [[...accum[0], item], accum[1]]
        : [accum[0], [...accum[1], item]],
    [[] as T[], [] as T[]],
  );
}

export function groupArray<T>(array: T[], groupFn: (item: T) => unknown) {
  return [
    ...array
      .reduce((accum, item) => {
        const groupingValue = groupFn(item);
        const group = accum.get(groupingValue) ?? [];
        return accum.set(groupingValue, [...group, item]);
      }, new Map<unknown, T[]>())
      .values(),
  ];
}

export function chunkArray<T>(
  array: T[],
  chunkFn: (lastItem: T, newItem: T) => boolean,
) {
  if (array.length === 0) {
    return [array];
  }

  return array.slice(1).reduce(
    (accum, item) => {
      const lastChunk = accum[accum.length - 1];
      const lastItem = lastChunk[lastChunk.length - 1];
      if (chunkFn(lastItem, item)) {
        lastChunk.push(item);
      } else {
        accum.push([item]);
      }
      return accum;
    },
    [[array[0]]],
  );
}

export function dedupeArray<T>(array: T[], dedupeFn: (item: T) => unknown) {
  return [
    ...array
      .reduce(
        (accum, item) => accum.set(dedupeFn(item), item),
        new Map<unknown, T>(),
      )
      .values(),
  ];
}

export function enumToField(
  enumType: Record<string, string>,
  formatFn: (enumValue: string) => string,
): [{ label: string; id: string }[], Map<string, string>] {
  const dropdownOptions = Object.values(enumType).map((value) => ({
    label: formatFn(value),
    id: value,
  }));

  const labelMap = new Map(dropdownOptions.map((o) => [o.label, o.id]));

  return [dropdownOptions, labelMap];
}

export function getConsumerDisplayName(consumer: {
  firstname: string;
  lastname: string;
  preferredFirstName?: string | null | undefined;
  preferredLastName?: string | null | undefined;
}) {
  const builtString = `${consumer.preferredFirstName ? consumer.preferredFirstName : consumer.firstname} ${consumer.preferredLastName ? consumer.preferredLastName : consumer.lastname}`;
  return builtString.trim();
}

export function getConsumerDisplayNameParts(consumer: {
  firstname: string;
  lastname: string;
  preferredFirstName?: string | null | undefined;
  preferredLastName?: string | null | undefined;
}) {
  const fname = consumer.preferredFirstName
    ? consumer.preferredFirstName
    : consumer.firstname;
  const lname = consumer.preferredLastName
    ? consumer.preferredLastName
    : consumer.lastname;
  return { fname, lname };
}

// biome-ignore lint/suspicious/noExplicitAny: this should work for any type of object
export function isDeepEqual(obj1: any, obj2: any): boolean {
  if (obj1 === obj2) return true;
  if (obj1 == null || obj2 == null) return obj1 === obj2;
  if (typeof obj1 !== typeof obj2) return false;

  // Special case for Date objects using date-fns
  if (obj1 instanceof Date && obj2 instanceof Date) {
    return isEqual(obj1, obj2);
  }

  if (typeof obj1 !== "object") return obj1 === obj2;

  if (Array.isArray(obj1)) {
    if (!Array.isArray(obj2) || obj1.length !== obj2.length) return false;
    return obj1.every((val, idx) => isDeepEqual(val, obj2[idx]));
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  return keys1.every(
    (key) =>
      Object.prototype.hasOwnProperty.call(obj2, key) &&
      isDeepEqual(obj1[key], obj2[key]),
  );
}

export const otherComparator = (
  a: { label: string; id: string },
  b: { label: string; id: string },
) => {
  if (a.label.toLowerCase() === "decline to answer") return 1;
  if (b.label.toLowerCase() === "decline to answer") return -1;
  if (a.label.toLowerCase() === "other") return 1;
  if (b.label.toLowerCase() === "other") return -1;
  if (a.label.toLowerCase() === "unknown") return 1;
  if (b.label.toLowerCase() === "unknown") return -1;
  return a.label.localeCompare(b.label);
};

export function responsiveTextSize(
  text: string,
  { sm = 20, md = 30 }: { sm?: number; md?: number } = {},
): string {
  if (text.length >= md) return "text-lg";
  if (text.length >= sm) return "text-xl";
  return "text-2xl";
}

export const formatEnum = (value: string, capitalize = true) => {
  const prettyValue = value.split("_").join(" ");
  if (!capitalize) return prettyValue.toLowerCase();

  return capitalizeProperly(prettyValue);
};

export const capitalizeProperly = (value: string) => {
  return value
    .split(" ")
    .map((word) =>
      ["to", "answer", "or"].includes(word.toLowerCase())
        ? word.toLowerCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join(" ");
};

/**
 * Wraps a promise in a try/catch block and logs the error to datadog if the code is running on the client, along with the error digest and function name
 * @param promise - The promise to execute
 * @param functionName - The name of the function to log the error for
 * @returns The result of the promise
 */
export async function tryCatchRethrow<T>(
  promise: Promise<T>,
  functionName?: string,
): Promise<T> {
  try {
    return await promise;
  } catch (error) {
    if (typeof window !== "undefined") {
      // when running on the client, we want to log the error to datadog
      datadogLogs.logger.error(
        `tryCatchRethrow Error: function '${functionName}' error: ${error instanceof Error ? error.message : error}`,
        {
          functionName,
        },
      );
    } else {
      // when running on the server, we want to log the error to the console
      console.error(
        `tryCatchRethrow Error: function '${functionName}', error message: ${error instanceof Error ? error.message : error}\n`,
        error,
      );
    }
    throw error;
  }
}
